/**
 * AdminChurn — /admin/churn
 *
 * Growth-team dashboard grouping cancellation reasons by frequency. Reads
 * from GET /api/admin/churn?days=30 (or 90). Auditor scope — read-only.
 *
 * Layout (Fortune-500 clean):
 *   • Top row  — 3 KPI tiles (total cancellations, trend vs prior window,
 *                 % of cancels that gave a reason)
 *   • Middle   — bar list of top reasons (sorted desc) + tiny sparkline
 *                 showing daily cancellations in the window
 *   • Bottom   — recent 20 rows table (redacted email tail)
 */
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { PhaseBanner } from "@/components/PhaseBanner";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const C = {
  bg: "#0F1829",
  surface: "#131F33",
  border: "#1E2F48",
  text: "#E8EFF8",
  muted: "#8AA5C2",
  cyan: "#00D4FF",
  green: "#00C896",
  amber: "#F59E0B",
  red: "#EF4444",
};

export default function AdminChurn() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null);
    api.get(`/admin/churn?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load churn data"));
  }, [days]);

  return (
    <AppShell>
      <div data-testid="admin-churn-page" style={{ padding: 24, color: C.text, minHeight: "100vh" }}>
        <PhaseBanner />        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end",
          marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>
              Churn — why people leave
            </h1>
            <p style={{ margin: "6px 0 0", color: C.muted, fontSize: 13, lineHeight: 1.5 }}>
              Cancellation reasons grouped by frequency. Sourced live from
              audit-events → billing.cancelled.
            </p>
          </div>
          <div data-testid="churn-window-tabs" role="tablist" style={{ display: "flex", gap: 4,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 3 }}>
            {[30, 90].map((n) => (
              <button key={n} data-testid={`churn-window-${n}`} role="tab"
                aria-selected={days === n}
                onClick={() => setDays(n)}
                style={{
                  padding: "7px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                  border: "none", cursor: "pointer",
                  background: days === n ? C.cyan : "transparent",
                  color: days === n ? "#0F1829" : C.muted,
                }}>Past {n}d</button>
            ))}
          </div>
        </header>

        {err && <div data-testid="churn-error" style={{ color: C.red, background: `${C.red}22`,
          padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>⚠ {err}</div>}

        {!data && !err && (
          <div data-testid="churn-loading" style={{ color: C.muted, padding: 40, textAlign: "center", fontSize: 13 }}>
            Loading…
          </div>
        )}

        {data && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 16, marginBottom: 24 }}>
              <KpiTile testid="churn-total" label="Cancellations"
                value={data.total_cancellations.toLocaleString()}
                sub={`in the last ${data.window_days} days`}
                accent={C.cyan} />
              <KpiTile testid="churn-trend" label="Trend vs prior window"
                value={data.trend_pct == null ? "—" : `${data.trend_pct > 0 ? "+" : ""}${data.trend_pct}%`}
                sub={`prior ${data.window_days}d: ${data.prior_window_total}`}
                accent={data.trend_pct == null ? C.muted
                        : data.trend_pct > 0 ? C.red : C.green} />
              <KpiTile testid="churn-reasoned" label="Gave a reason"
                value={data.reasoned_pct == null ? "—" : `${data.reasoned_pct}%`}
                sub="silent churn otherwise"
                accent={C.amber} />
            </div>

            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)",
              alignItems: "start", marginBottom: 24 }}>
              <ReasonsList data={data} />
              <PlanBreakdown data={data} />
            </div>

            <RecentRows data={data} />
          </>
        )}
      </div>
    </AppShell>
  );
}

function KpiTile({ testid, label, value, sub, accent }) {
  return (
    <div data-testid={testid} style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: "18px 20px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent }} />
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.06em", fontWeight: 700,
        textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: C.text, letterSpacing: "-0.02em",
        fontFamily: "'Space Grotesk',sans-serif", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{sub}</div>
    </div>
  );
}

function ReasonsList({ data }) {
  const max = data.top_reasons[0]?.count || 1;
  return (
    <div data-testid="churn-reasons" style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20,
    }}>
      <h2 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: C.text,
        letterSpacing: "0.02em", textTransform: "uppercase" }}>
        Top reasons
      </h2>
      {data.top_reasons.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13, padding: 20, textAlign: "center" }}>
          No cancellations in this window.
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {data.top_reasons.map((r, i) => (
            <li key={i} data-testid={`churn-reason-${i}`}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: C.text, maxWidth: "70%", overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap",
                  fontStyle: r.reason.startsWith("(no reason") ? "italic" : "normal",
                  opacity: r.reason.startsWith("(no reason") ? 0.7 : 1 }} title={r.reason}>
                  {r.reason}
                </span>
                <span style={{ color: C.muted, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                  {r.count} · {r.pct}%
                </span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: `${C.cyan}22`, overflow: "hidden" }}>
                <div style={{ width: `${(r.count / max) * 100}%`, height: "100%", background: C.cyan }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PlanBreakdown({ data }) {
  const total = data.total_cancellations || 1;
  return (
    <div data-testid="churn-by-plan" style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20,
    }}>
      <h2 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: C.text,
        letterSpacing: "0.02em", textTransform: "uppercase" }}>
        By plan
      </h2>
      {data.by_plan.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13, padding: 20, textAlign: "center" }}>—</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {data.by_plan.map((p, i) => (
            <li key={p.plan} data-testid={`churn-plan-${p.plan}`}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: C.text, textTransform: "capitalize", fontWeight: 600 }}>
                  {p.plan}
                </span>
                <span style={{ color: C.muted, fontVariantNumeric: "tabular-nums" }}>
                  {p.count} · {Math.round(p.count / total * 100)}%
                </span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: `${T.dark.muted}22`, overflow: "hidden" }}>
                <div style={{ width: `${(p.count / total) * 100}%`, height: "100%",
                  background: i === 0 ? C.amber : C.muted }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentRows({ data }) {
  return (
    <div data-testid="churn-recent" style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20,
    }}>
      <h2 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: C.text,
        letterSpacing: "0.02em", textTransform: "uppercase" }}>
        Most recent (20)
      </h2>
      {data.recent.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13, padding: 20, textAlign: "center" }}>—</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: "8px 6px", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.06em", fontSize: 10 }}>Date</th>
                <th style={{ padding: "8px 6px", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.06em", fontSize: 10 }}>Plan</th>
                <th style={{ padding: "8px 6px", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.06em", fontSize: 10 }}>Email tail</th>
                <th style={{ padding: "8px 6px", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.06em", fontSize: 10 }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r, i) => (
                <tr key={i} data-testid={`churn-row-${i}`}
                  style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "8px 6px", color: C.text, whiteSpace: "nowrap" }}>{r.date}</td>
                  <td style={{ padding: "8px 6px", color: C.text, textTransform: "capitalize" }}>{r.plan || "—"}</td>
                  <td style={{ padding: "8px 6px", color: C.muted, fontFamily: "monospace", fontSize: 11 }}>
                    …{r.email_tail}
                  </td>
                  <td style={{ padding: "8px 6px", color: C.text, maxWidth: 400,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    fontStyle: r.reason ? "normal" : "italic",
                    opacity: r.reason ? 1 : 0.55 }}>
                    {r.reason || "(no reason given)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
